import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { ArrowRight } from "@acto/web-common/components";
import type { MeetingPrepFeedbackReason } from "@acto/taskpane/core/analytics";
import { Card } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { OutlinedPillButton } from "@acto/taskpane/field-sales-2/components/primitives/OutlinedPillButton";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { LabeledTextArea } from "@acto/taskpane/field-sales-2/components/primitives/LabeledTextArea";
import { shellBarsScrollMargin } from "@acto/taskpane/field-sales-2/styles/shellBarsScrollMargin";
import { shellTopBarHeight } from "@acto/taskpane/field-sales-2/styles/shellTopBarHeight";
import { scrollIntoClearView } from "@acto/taskpane/field-sales-2/utils/scrollIntoClearView";
import { useKeyboardField } from "@acto/taskpane/field-sales-2/utils/hooks/useKeyboardField";

export type HideReasonKind = "contentNotRelevant" | "dataIncorrect";

export interface HideReasonChoice {
  kind: HideReasonKind;
  text: string;
}

export const TRACKED_REASON_BY_KIND: Record<HideReasonKind, MeetingPrepFeedbackReason> = {
  contentNotRelevant: "content_irrelevant",
  dataIncorrect: "data_incorrect",
};

const REASONS: readonly HideReasonKind[] = ["contentNotRelevant", "dataIncorrect"];

const Frame = styled(Card)(({ theme }) => ({
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[32],
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[16]}`,
  ...shellBarsScrollMargin(theme),
}));

const Question = styled("p")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.fieldSalesTwo.spacing[12],
  paddingRight: theme.fieldSalesTwo.spacing[12],
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["17"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

const Options = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const ReasonButton = styled(OutlinedPillButton)(({ theme }) => ({
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
}));

const CancelButton = styled("button")(({ theme }) => ({
  height: theme.fieldSalesTwo.spacing[50],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "transparent",
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  transition: "background 120ms ease, transform 120ms ease",
  "&:hover": {
    background: theme.fieldSalesTwo.colors.surface.glassMedium,
  },
  ...pressInteraction(theme),
}));

// The chosen reason turns into the answer field, labelled with its own follow-up question.
const InputCard = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius["20"],
  background: theme.fieldSalesTwo.colors.neutral[200],
  scrollMarginTop: shellTopBarHeight(theme),
  scrollMarginBottom: theme.fieldSalesTwo.spacing[16],
}));

const SubmitButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "34px",
  height: "34px",
  flexShrink: 0,
  padding: 0,
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.accent.action,
  color: theme.colors.neutral[0],
  cursor: "pointer",
  transition: "opacity 120ms ease, transform 120ms ease",
  ...pressInteraction(theme),
  "&:disabled": {
    opacity: 0.4,
    cursor: "default",
  },
  "& svg": { width: "14px", height: "auto" },
}));

interface ReasonPromptProps {
  question: string;
  /** Locale namespace holding the reason, input-label, submit and cancel keys; defaults to the card-hide copy. */
  namespace?: string;
  onSelect: (choice: HideReasonChoice) => void;
  onCancel: () => void;
}

/**
 * Two-reason feedback picker with a mandatory free-text step on both reasons.
 * Picking a reason turns that pill into the answer field in place; the other
 * reason stays available, and the choice only submits with non-empty text.
 */
export function ReasonPrompt({
  question,
  namespace = "fieldSalesTwo.genuiMeetingPrep.cardHide",
  onSelect,
  onCancel,
}: ReasonPromptProps) {
  const t = useTranslations(namespace);
  const [selectedKind, setSelectedKind] = useState<HideReasonKind | null>(null);
  const [text, setText] = useState("");
  const frameRef = useRef<HTMLElement>(null);
  const answerField = useKeyboardField<HTMLFormElement>();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const fieldHeightRef = useRef<number | null>(null);

  useEffect(() => {
    scrollIntoClearView(frameRef.current);
  }, []);

  useEffect(() => {
    if (!selectedKind) return;
    textAreaRef.current?.focus({ preventScroll: true });
    scrollIntoClearView(frameRef.current);
  }, [selectedKind]);

  const handleFieldHeight = (height: number) => {
    const fieldResized = fieldHeightRef.current !== null && height !== fieldHeightRef.current;
    // The whole prompt rarely fits above the keyboard.
    if (fieldResized && !answerField.reveal()) scrollIntoClearView(frameRef.current);
    fieldHeightRef.current = height;
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!selectedKind || !trimmed) return;
    onSelect({ kind: selectedKind, text: trimmed });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const labelFor = (kind: HideReasonKind) =>
    kind === "dataIncorrect" ? t("reasonInputLabelDataIncorrect") : t("reasonInputLabelNotRelevant");

  const reasonLabel = (kind: HideReasonKind) =>
    kind === "dataIncorrect" ? t("reasonDataIncorrect") : t("reasonContentNotRelevant");

  return (
    <Frame ref={frameRef} role="group" aria-label={question}>
      <Question>{question}</Question>
      <Options>
        {REASONS.map((kind) =>
          kind === selectedKind ? (
            <InputCard key={kind} {...answerField.props} onSubmit={handleSubmit}>
              <LabeledTextArea
                id={`hide-reason-${kind}`}
                ref={textAreaRef}
                label={labelFor(kind)}
                value={text}
                onChange={(event) => setText(event.target.value)}
                onHeightChange={handleFieldHeight}
                onKeyDown={handleKeyDown}
                enterKeyHint="send"
              />
              <SubmitButton type="submit" disabled={!text.trim()} aria-label={t("reasonSubmit")}>
                <ArrowRight aria-hidden="true" />
              </SubmitButton>
            </InputCard>
          ) : (
            <ReasonButton
              key={kind}
              type="button"
              onClick={() => {
                setSelectedKind(kind);
                setText("");
                fieldHeightRef.current = null;
              }}
            >
              {reasonLabel(kind)}
            </ReasonButton>
          ),
        )}
        <CancelButton type="button" onClick={onCancel}>
          {t("cancel")}
        </CancelButton>
      </Options>
    </Frame>
  );
}
